import { useMemo } from "react";
import type { InspectionWithProfiles, Defect, DashboardStats } from "@/hooks/useDashboardQuery";
import { buildBentoView, type GetDisplayStatus } from "./dashboardBento.helpers";
import { DashboardBentoDesktop } from "./DashboardBentoDesktop";
import { DashboardBentoMobile } from "./DashboardBentoMobile";

/**
 * DashboardBento — S1 (TES-954): "Industrial Management Portal" Dashboard-Kern.
 *
 * Pixelgenaue Umsetzung der Stitch-Mocks screen-04 (Desktop 12-col Bento) und
 * screen-09 (Mobile Hero-Stack + 2-up + horizontaler MPP-Scroll + Recent-Cards).
 * Diese Huelle leitet die Anzeige-Werte EINMAL ab (buildBentoView) und rendert
 * beide Breakpoint-Layouts (CSS-toggled via md:hidden / hidden md:grid).
 *
 * STRIKT real-schema-treu: alle Status/Dots/Texte werden aus realem
 * `inspection.status` / `defect.*` via `getDisplayStatus` abgeleitet — KEIN
 * neuer Enum, KEINE erfundene "SAP-Sync"/"PT-TSK"/"Gate"-Entitaet. Das
 * Plasser-Vokabular der Mocks (MPP) ist hier nur ein i18n-Anzeige-Label.
 */

interface DashboardBentoProps {
  recentInspections: InspectionWithProfiles[];
  criticalDefects: Defect[];
  stats: DashboardStats | null | undefined;
  defectsOpen: number;
  /** Abgeleiteter Anzeige-Status einer Pruefung (Dashboard.getDisplayStatus). */
  getDisplayStatus: GetDisplayStatus;
}

export function DashboardBento({
  recentInspections,
  criticalDefects,
  stats,
  defectsOpen,
  getDisplayStatus,
}: DashboardBentoProps) {
  const view = useMemo(
    () => buildBentoView(recentInspections, criticalDefects, stats, defectsOpen),
    [recentInspections, criticalDefects, stats, defectsOpen],
  );

  return (
    <div data-testid="dashboard-bento">
      <DashboardBentoMobile
        recentInspections={recentInspections}
        view={view}
        getDisplayStatus={getDisplayStatus}
      />
      <DashboardBentoDesktop
        recentInspections={recentInspections}
        defectsOpen={defectsOpen}
        view={view}
        getDisplayStatus={getDisplayStatus}
      />
    </div>
  );
}
